import { useEffect, useId, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Bot, ChevronDown, LayoutDashboard, Bookmark, LogIn, LogOut, Menu, Search, Settings, User, X } from 'lucide-react'
import { motion, useReducedMotion } from 'framer-motion'
import { Logo } from '@/shared/ui/logo'
import { Button } from '@/shared/ui/button'
import { LocaleToggle, ThemeToggle } from '@/shared/ui/theme-toggle'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { cn } from '@/shared/lib/cn'
import { useSearch } from '@/shared/search/search-context-store'
import {
  useCurrentUser,
  useIsAuthenticated,
} from '@/features/auth/store'
import {
  getHiddenPaths,
  usePageStatusStore,
} from '@/features/page-status/store'
import { logout as performLogout } from '@/features/auth/api'

const T = defineT({
  home: { ar: 'الرئيسية', en: 'Home' },
  learn: { ar: 'تعلّم', en: 'Learn' },
  courses: { ar: 'الدورات', en: 'Courses' },
  tutorials: { ar: 'شروحات', en: 'Tutorials' },
  library: { ar: 'المكتبة', en: 'Library' },
  prompts: { ar: 'البرومبتات', en: 'Prompts' },
  toolshub: { ar: 'دليل الأدوات', en: 'Tools Hub' },
  vibeCoding: { ar: 'Vibe Coding', en: 'Vibe Coding' },
  vibeSkills: { ar: 'أدوات Claude Code', en: 'Claude Code Skills' },
  designSystems: { ar: 'Design Systems', en: 'Design Systems' },
  zatoona: { ar: 'الزتونة', en: 'Zatoona' },
  promptEngineering: { ar: 'هندسة الأوامر', en: 'Prompt Engineering' },
  promptGuide: { ar: 'إزاي تكتب برومبت', en: 'How to Prompt' },
  freeApis: { ar: 'دليل APIs مجانية', en: 'Free APIs' },
  glossary: { ar: 'قاموس الـ AI', en: 'AI Glossary' },
  aiNews: { ar: 'أخبار الـ AI', en: 'AI News' },
  assistant: { ar: 'المساعد الذكي', en: 'AI Assistant' },
  myTools: { ar: 'الأدوات', en: 'Tools' },
  proTools: { ar: 'أدوات احترافية', en: 'Pro Tools' },
  freeTools: { ar: 'أدوات مجانية', en: 'Free Tools' },
  allTools: { ar: 'كل الأدوات', en: 'All Tools' },
  objectTalkStudio: { ar: 'Object Talk Studio', en: 'Object Talk Studio' },
  aiSearch: { ar: 'باحث', en: 'Bahath' },
  competitorIQ: { ar: 'دراسة المنافسين', en: 'Competitor Study' },
  blog: { ar: 'المقالات', en: 'Blog' },
  aiCourse: { ar: 'كورس AI', en: 'AI Course' },
  more: { ar: 'المزيد', en: 'More' },
  studio: { ar: 'الاستوديو', en: 'Studio' },
  about: { ar: 'من أنا', en: 'About' },
  contact: { ar: 'تواصل', en: 'Contact' },
  start: { ar: 'ابدأ الآن', en: 'Get Started' },
  signIn: { ar: 'تسجيل الدخول', en: 'Sign in' },
  menu: { ar: 'القائمة', en: 'Menu' },
  close: { ar: 'إغلاق', en: 'Close' },
  account: { ar: 'حسابي', en: 'Account' },
  dashboard: { ar: 'لوحتي', en: 'Dashboard' },
  myLibrary: { ar: 'مكتبتي', en: 'My Library' },
  settings: { ar: 'الإعدادات', en: 'Settings' },
  logout: { ar: 'تسجيل الخروج', en: 'Sign out' },
  search: { ar: 'بحث', en: 'Search' },
})

interface NavItem {
  to: string
  key: keyof typeof T
  matchPaths?: string[]
  children?: { to: string; key: keyof typeof T }[]
}

const NAV: NavItem[] = [
  { to: '/', key: 'home' },
  {
    to: '#',
    key: 'learn',
    matchPaths: ['/courses', '/course', '/ai-course', '/tutorials'],
    children: [
      { to: '/courses', key: 'courses' },
      { to: '/ai-course', key: 'aiCourse' },
      { to: '/tutorials', key: 'tutorials' },
    ],
  },
  {
    to: '#',
    key: 'library',
    children: [
      { to: '/prompts', key: 'prompts' },
      { to: '/toolshub', key: 'toolshub' },
      { to: '/zatoona', key: 'zatoona' },
      { to: '/prompt-engineering', key: 'promptEngineering' },
      { to: '/glossary', key: 'glossary' },
      { to: '/ai-news', key: 'aiNews' },
      { to: '/assistant', key: 'assistant' },
    ],
  },
  {
    to: '#',
    key: 'vibeCoding',
    matchPaths: ['/vibe-coding', '/design-systems', '/free-apis'],
    children: [
      { to: '/vibe-coding', key: 'vibeSkills' },
      { to: '/design-systems', key: 'designSystems' },
      { to: '/free-apis', key: 'freeApis' },
    ],
  },
  {
    to: '#',
    key: 'myTools',
    matchPaths: ['/tools', '/freetools', '/toolshub', '/freetools/object-talk-studio', '/freetools/ai-search'],
    children: [
      { to: '/tools', key: 'proTools' },
      { to: '/freetools', key: 'freeTools' },
      { to: '/toolshub', key: 'toolshub' },
      { to: '/freetools/ai-search', key: 'aiSearch' },
      { to: '/freetools/competitor-intelligence', key: 'competitorIQ' },
      { to: '/freetools/object-talk-studio', key: 'objectTalkStudio' },
    ],
  },
  { to: '/blog', key: 'blog' },
  { to: '/studio', key: 'studio' as any },
  {
    to: '#',
    key: 'more',
    children: [
      { to: '/about', key: 'about' },
      { to: '/contact', key: 'contact' },
    ],
  },
]

export function Navbar() {
  const { t } = useTranslation(T)
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileGroups, setMobileGroups] = useState<Set<string>>(new Set())
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const indicatorId = useId()
  const reducedMotion = useReducedMotion()
  const location = useLocation()
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const currentUser = useCurrentUser()
  const { open: openSearch } = useSearch()

  // Read the page-status table once. Admins see everything.
  const pageStatusList = usePageStatusStore((s) => s.list)
  const hiddenPaths =
    currentUser?.role === 'admin' ? new Set<string>() : getHiddenPaths(pageStatusList)

  /**
   * Filter NAV in place: drop a leaf item if its path is hidden, drop
   * a dropdown if all of its children are hidden. Admins see the full
   * list.
   */
  const visibleNav = NAV.filter((item) => {
    if (item.children) {
      return item.children.some((c) => !hiddenPaths.has(c.to))
    }
    if (item.to === '#' || item.to === '/') return true
    return !hiddenPaths.has(item.to)
  }).map((item) => {
    if (!item.children) return item
    return {
      ...item,
      children: item.children.filter((c) => !hiddenPaths.has(c.to)),
    }
  })

  // Sticky-on-scroll style swap
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  // Close dropdown / mobile when route changes
  useEffect(() => {
    setOpenDropdown(null)
    setMobileOpen(false)
    setAccountOpen(false)
  }, [location.pathname])

  // Close account menu on outside click
  useEffect(() => {
    if (!accountOpen) return
    const onClick = (e: MouseEvent) => {
      if (!accountRef.current?.contains(e.target as Node)) {
        setAccountOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [accountOpen])

  const handleLogout = () => {
    void performLogout()
    setAccountOpen(false)
    navigate('/')
  }

  const initials = (currentUser?.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  // Decide which nav item is "active" for the floating indicator
  const activeKey = (() => {
    const path = location.pathname
    for (const item of NAV) {
      if (item.children) {
        const hit = item.children.some((c) =>
          path === c.to || path.startsWith(`${c.to}/`),
        )
        if (hit) return item.key
        continue
      }
      if (item.to === '/') {
        if (path === '/') return item.key
        continue
      }
      const matches = item.matchPaths ?? [item.to]
      if (matches.some((m) => path === m || path.startsWith(`${m}/`))) {
        return item.key
      }
    }
    return null
  })()

  // The pill follows hover when the user is hovering nav, otherwise the route
  const indicatorKey = hoveredKey ?? activeKey

  // On mobile menu open, auto-expand the group that holds the active route
  useEffect(() => {
    if (mobileOpen) {
      setMobileGroups(new Set(activeKey ? [activeKey] : []))
    }
  }, [mobileOpen, activeKey])

  const toggleMobileGroup = (key: string) =>
    setMobileGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <>
      {/* Top decorative aurora hairline (non-interactive) */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none fixed inset-x-0 top-0 z-40 h-px',
          'bg-gradient-to-r from-transparent via-primary/60 to-transparent',
          'transition-opacity duration-500',
          scrolled ? 'opacity-100' : 'opacity-0',
        )}
      />

      <header
        className={cn(
          'fixed inset-x-0 z-50',
          'transition-[padding,top] duration-300 ease-out',
          scrolled ? 'top-3 sm:top-4' : 'top-0',
        )}
      >
        <div
          className={cn(
            'mx-auto flex items-center gap-2 transition-all duration-300 ease-out',
            scrolled
              ? 'h-14 max-w-6xl rounded-full border border-border/60 bg-background/70 px-3 shadow-lg shadow-black/20 backdrop-blur-xl supports-[backdrop-filter]:bg-background/55'
              : 'h-16 max-w-7xl px-4 sm:px-6 lg:px-8',
          )}
        >
          {/* Logo */}
          <Link
            to="/"
            aria-label="Abdallah Issa"
            className="group/logo relative shrink-0 outline-none"
          >
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0 -z-10 rounded-full bg-primary/30 opacity-0 blur-xl',
                'transition-opacity duration-300',
                'group-hover/logo:opacity-100 group-focus-visible/logo:opacity-100',
              )}
            />
            <Logo size={scrolled ? 32 : 36} showWordmark={!scrolled} />
          </Link>

          {/* Desktop nav (centered pill) */}
          <nav
            aria-label="Primary"
            onMouseLeave={() => setHoveredKey(null)}
            className="ms-auto hidden items-center lg:flex"
          >
            <ul className="relative flex items-center gap-0.5 rounded-full border border-border/40 bg-card/40 p-1 backdrop-blur-md">
              {visibleNav.map((item) => {
                const isActiveOrHovered = indicatorKey === item.key
                if (item.children) {
                  return (
                    <li
                      key={item.key}
                      className="relative"
                      onMouseEnter={() => {
                        setHoveredKey(item.key)
                        setOpenDropdown(item.key)
                      }}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button
                        type="button"
                        aria-expanded={openDropdown === item.key}
                        aria-haspopup="menu"
                        className={cn(
                          'relative inline-flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-medium',
                          'transition-colors duration-200',
                          isActiveOrHovered
                            ? 'text-foreground'
                            : 'text-foreground/70 hover:text-foreground',
                        )}
                      >
                        {isActiveOrHovered && (
                          <motion.span
                            layoutId={indicatorId}
                            className="absolute inset-0 -z-10 rounded-full bg-secondary/80 ring-1 ring-border/60"
                            transition={
                              reducedMotion
                                ? { duration: 0 }
                                : { type: 'spring', stiffness: 380, damping: 32 }
                            }
                          />
                        )}
                        <span className="relative z-10">{t(item.key)}</span>
                        <ChevronDown
                          className={cn(
                            'relative z-10 h-3.5 w-3.5 transition-transform duration-200',
                            openDropdown === item.key && 'rotate-180',
                          )}
                          aria-hidden
                        />
                      </button>

                      {/* Dropdown */}
                      <div
                        className={cn(
                          'absolute start-0 top-full pt-3',
                          'origin-top transition-all duration-200',
                          openDropdown === item.key
                            ? 'pointer-events-auto translate-y-0 opacity-100'
                            : 'pointer-events-none -translate-y-1 opacity-0',
                        )}
                        role="menu"
                      >
                        <div className="min-w-[15rem] overflow-hidden rounded-2xl border border-border/60 bg-popover/90 p-1.5 shadow-2xl shadow-black/40 backdrop-blur-xl">
                          {item.children.map((c) => (
                            <NavLink
                              key={c.key}
                              to={c.to}
                              role="menuitem"
                              className={({ isActive }) =>
                                cn(
                                  'block rounded-xl px-3 py-2.5 text-sm transition-colors duration-150',
                                  isActive
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-foreground/80 hover:bg-secondary hover:text-foreground',
                                )
                              }
                            >
                              {t(c.key)}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </li>
                  )
                }
                return (
                  <li
                    key={item.key}
                    onMouseEnter={() => setHoveredKey(item.key)}
                  >
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'relative inline-flex h-9 items-center rounded-full px-3.5 text-sm font-medium',
                          'transition-colors duration-200',
                          isActive || isActiveOrHovered
                            ? 'text-foreground'
                            : 'text-foreground/70 hover:text-foreground',
                        )
                      }
                    >
                      {isActiveOrHovered && (
                        <motion.span
                          layoutId={indicatorId}
                          className="absolute inset-0 -z-10 rounded-full bg-secondary/80 ring-1 ring-border/60"
                          transition={
                            reducedMotion
                              ? { duration: 0 }
                              : { type: 'spring', stiffness: 380, damping: 32 }
                          }
                        />
                      )}
                      <span className="relative z-10">{t(item.key)}</span>
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </nav>

          {/* Right cluster */}
          <div className="ms-auto flex items-center gap-2 lg:ms-3">
            {/* Search trigger — pill on desktop, icon on mobile */}
            <button
              type="button"
              onClick={openSearch}
              aria-label={t('search')}
              className={cn(
                'group/search inline-flex h-9 items-center gap-2 rounded-full border border-border bg-card/60 text-foreground/70 backdrop-blur-md',
                'transition-colors duration-200 hover:border-primary/40 hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                'w-9 justify-center sm:w-auto sm:justify-start sm:px-3',
              )}
            >
              <Search className="h-4 w-4" aria-hidden />
              <span className="hidden text-sm sm:inline">{t('search')}</span>
              <kbd className="hidden items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground lg:inline-flex">
                ⌘K
              </kbd>
            </button>
            <Link
              to="/library"
              aria-label={t('library')}
              title={t('library')}
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card/60 text-foreground/70 backdrop-blur-md',
                'transition-colors duration-200 hover:border-primary/40 hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              )}
            >
              <Bookmark className="h-4 w-4" aria-hidden />
            </Link>
            {/*
              Assistant. Tinted rather than neutral like the icons around it:
              it is the only one that opens an AI surface, and the primary
              accent is what signals that without needing a label.
            */}
            <Link
              to="/assistant"
              aria-label={t('assistant')}
              title={t('assistant')}
              className={cn(
                // Hidden on the narrowest screens: five icons plus the menu
                // button crowds the bar, and the floating button already
                // covers mobile access.
                'hidden h-9 w-9 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary backdrop-blur-md sm:inline-flex',
                'transition-colors duration-200 hover:border-primary/50 hover:bg-primary/15',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              )}
            >
              <Bot className="h-4 w-4" aria-hidden />
            </Link>
            <LocaleToggle size="sm" />
            <ThemeToggle size="sm" />
            {isAuthenticated ? (
              <div ref={accountRef} className="relative hidden sm:block">
                <button
                  type="button"
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  aria-label={t('account')}
                  className={cn(
                    'inline-flex h-9 items-center gap-2 rounded-full border border-border bg-card/60 ps-1 pe-3 text-sm font-medium text-foreground/90 backdrop-blur-md',
                    'transition-colors duration-200 hover:border-primary/40 hover:text-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                  )}
                >
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/15 text-[11px] font-bold tabular-nums text-primary ring-1 ring-primary/30">
                    {initials || <User className="h-3.5 w-3.5" aria-hidden />}
                  </span>
                  <span className="max-w-[8rem] truncate">
                    {currentUser?.name?.split(' ')[0]}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 transition-transform duration-200',
                      accountOpen && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </button>
                <div
                  role="menu"
                  className={cn(
                    'absolute end-0 top-full mt-2 min-w-[14rem] origin-top overflow-hidden rounded-2xl border border-border/60 bg-popover/95 p-1.5 shadow-2xl shadow-black/40 backdrop-blur-xl',
                    'transition-all duration-200',
                    accountOpen
                      ? 'pointer-events-auto translate-y-0 opacity-100'
                      : 'pointer-events-none -translate-y-1 opacity-0',
                  )}
                >
                  <div className="px-3 py-2">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {currentUser?.name}
                    </p>
                    <p
                      className="truncate text-xs text-muted-foreground"
                      dir="ltr"
                    >
                      {currentUser?.email}
                    </p>
                  </div>
                  <div className="my-1 h-px bg-border/60" aria-hidden />
                  <Link
                    to="/student/dashboard"
                    role="menuitem"
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-foreground"
                  >
                    <LayoutDashboard className="h-4 w-4" aria-hidden />
                    {t('dashboard')}
                  </Link>
                  <Link
                    to="/student/library"
                    role="menuitem"
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-foreground"
                  >
                    <Bookmark className="h-4 w-4" aria-hidden />
                    {t('myLibrary')}
                  </Link>
                  <Link
                    to="/student/settings"
                    role="menuitem"
                    className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-foreground/80 hover:bg-secondary hover:text-foreground"
                  >
                    <Settings className="h-4 w-4" aria-hidden />
                    {t('settings')}
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-foreground/80 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    {t('logout')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className={cn(
                    'hidden h-9 items-center gap-1.5 rounded-full border border-border bg-card/60 px-3.5 text-sm font-medium text-foreground/80 backdrop-blur-md',
                    'transition-colors duration-200 hover:border-primary/40 hover:text-foreground',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'sm:inline-flex',
                  )}
                >
                  <LogIn className="h-3.5 w-3.5" aria-hidden />
                  {t('signIn')}
                </Link>
                <Link
                  to="/register"
                  className="hidden sm:inline-flex"
                  aria-label={t('start')}
                >
                  <Button size="sm" className="rounded-full" type="button">
                    {t('start')}
                  </Button>
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? t('close') : t('menu')}
              aria-expanded={mobileOpen}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card/60 backdrop-blur-md lg:hidden"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={cn(
          'fixed inset-0 z-40 lg:hidden',
          'transition-opacity duration-300',
          mobileOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0',
        )}
      >
        <button
          type="button"
          aria-label="Close menu overlay"
          tabIndex={mobileOpen ? 0 : -1}
          className="absolute inset-0 bg-background/80 backdrop-blur-md"
          onClick={() => setMobileOpen(false)}
        />
        <nav
          aria-label="Mobile primary"
          className={cn(
            'absolute inset-x-3 top-[4.75rem] flex max-h-[calc(100dvh-5.75rem)] flex-col overflow-hidden rounded-3xl border border-border/60 bg-popover/95 shadow-2xl shadow-black/40 backdrop-blur-2xl',
            'transition-all duration-300 ease-out',
            mobileOpen
              ? 'translate-y-0 opacity-100'
              : '-translate-y-2 opacity-0',
          )}
        >
          <div className="flex-1 overflow-y-auto overscroll-contain p-3">
            <ul className="space-y-0.5">
              {visibleNav.map((item) =>
                item.children ? (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => toggleMobileGroup(item.key)}
                      aria-expanded={mobileGroups.has(item.key)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-xl px-3 py-3 text-base font-semibold transition-colors',
                        activeKey === item.key
                          ? 'text-primary'
                          : 'text-foreground/90 hover:bg-secondary',
                      )}
                    >
                      <span>{t(item.key)}</span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                          mobileGroups.has(item.key) && 'rotate-180',
                        )}
                        aria-hidden
                      />
                    </button>
                    <div
                      className={cn(
                        'grid transition-all duration-300 ease-out',
                        mobileGroups.has(item.key)
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'grid-rows-[0fr] opacity-0',
                      )}
                    >
                      <ul className="overflow-hidden ps-3">
                        {item.children.map((c) => (
                          <li key={c.key}>
                            <NavLink
                              to={c.to}
                              className={({ isActive }) =>
                                cn(
                                  'flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-colors',
                                  'before:h-1.5 before:w-1.5 before:rounded-full before:bg-current before:opacity-40',
                                  isActive
                                    ? 'bg-primary/10 font-medium text-primary'
                                    : 'text-foreground/70 hover:bg-secondary hover:text-foreground',
                                )
                              }
                            >
                              {t(c.key)}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ) : (
                  <li key={item.key}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        cn(
                          'block rounded-xl px-3 py-3 text-base font-semibold transition-colors',
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'text-foreground/90 hover:bg-secondary',
                        )
                      }
                    >
                      {t(item.key)}
                    </NavLink>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="shrink-0 border-t border-border/60 bg-popover/95 p-3">
            {isAuthenticated ? (
              <div className="space-y-1">
                <div className="flex items-center gap-3 rounded-xl px-3 py-2">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary ring-1 ring-primary/30">
                    {initials || <User className="h-4 w-4" aria-hidden />}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {currentUser?.name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground" dir="ltr">
                      {currentUser?.email}
                    </p>
                  </div>
                </div>
                <Link
                  to="/student/dashboard"
                  className="flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium text-foreground/80 hover:bg-secondary"
                >
                  <LayoutDashboard className="h-4 w-4" aria-hidden />
                  {t('dashboard')}
                </Link>
                <Link
                  to="/student/settings"
                  className="flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium text-foreground/80 hover:bg-secondary"
                >
                  <Settings className="h-4 w-4" aria-hidden />
                  {t('settings')}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  {t('logout')}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link to="/register" className="block">
                  <Button size="lg" className="w-full">
                    {t('start')}
                  </Button>
                </Link>
                <Link
                  to="/login"
                  className="flex h-12 items-center justify-center gap-2 rounded-xl border border-border text-sm font-medium text-foreground/80 hover:bg-secondary"
                >
                  <LogIn className="h-4 w-4" aria-hidden />
                  {t('signIn')}
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>
    </>
  )
}
